import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Spinner } from "reactstrap";
import { ActionCreator, compose } from "redux";
import { Logout, Refresh } from "../actions/auth/Auth";
import { useDynamoDB } from "../hooks/useDynamoDB";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { LoginType } from "../types/auth";

interface Props {
  children: React.ReactNode;
  auth: IAuthReducer;
  Logout: ActionCreator<unknown>;
  Refresh: ActionCreator<unknown>;
}

/**
 * This component checks if the current date is after the session
 * date pulled from DynamoDB. If it is, the user is logged out.
 *
 * The purpose of this is to force a logout to reset the auth state
 * so that a new seasonal database is used. The seasonal database
 * update will need to happen before the session date is reached.
 *
 */
const SessionGuard = ({ children, auth, Logout, Refresh }: Props) => {
  const [valid, setValid] = useState(false);

  const isOnline = useOnlineStatus();
  const history = useHistory();
  const client = useMemo(
    () =>
      new DynamoDBClient({
        region: "ap-southeast-2",
        credentials: {
          accessKeyId: auth.access?.tempAwsCredentials.key || "",
          secretAccessKey: auth.access?.tempAwsCredentials.secret || "",
          sessionToken: auth.access?.tempAwsCredentials.token || "",
        },
      }),
    [
      auth.access?.tempAwsCredentials.key,
      auth.access?.tempAwsCredentials.secret,
      auth.access?.tempAwsCredentials.token,
    ]
  );
  const { loading, getItem, putItem, updateItem } = useDynamoDB({
    tableName: "trapping_app_user_settings",
    client,
  });

  const seasonStartDate = useMemo(() => {
    const today = moment();
    let startDate = moment({ year: today.year(), month: 8, day: 1 });
    if (today.isAfter(startDate, "day")) {
      startDate = startDate.add(1, "year");
    }
    return startDate.format("YYYY-MM-DD");
  }, []);

  const refresh = useCallback(async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    await Refresh({ service: auth.service, refreshToken: refreshToken || "" });
  }, [Refresh, auth.service]);

  const putInvalidationDate = useCallback(
    async (identifier: string, date: string) => {
      try {
        await putItem({
          identifier,
          invalidationDate: date,
        });
      } catch (error) {
        console.error("Error putting invalidation date", error);
      }
    },
    [putItem]
  );

  const getInvalidationDate = useCallback(async () => {
    try {
      const key = { identifier: `${auth.tenantName}-${auth.id}` };
      const item = await getItem(key);
      // No item found so create and return next season start date.
      if (!item) {
        putInvalidationDate(key.identifier, seasonStartDate);
        return seasonStartDate;
      }
      // Item found so return invalidationDate.
      const date = item.invalidationDate as string;
      if (date) {
        return date;
      }
      return null;
    } catch (error) {
      const err = error as Error;
      if (err.name === "ExpiredTokenException" || err.name === "UnrecognizedClientException") {
        throw err;
      } else {
        console.error("Error getting invalidation date", err);
      }
      return null;
    }
  }, [
    auth.id,
    auth.tenantName,
    getItem,
    putInvalidationDate,
    seasonStartDate,
  ]);

  const updateInvalidationDate = useCallback(
    async (identifier: string, date: string) => {
      try {
        await updateItem({ identifier }, { invalidationDate: date });
      } catch (error) {
        console.error("Error updating invalidation date", error);
      }
    },
    [updateItem]
  );

  const logout = useCallback(() => {
    Logout();
    if (auth.loginType === LoginType.Credentials) {
      history.push("/login");
    } else {
      history.push(`/${auth.tenantName}/grower-login`);
    }
  }, [Logout, auth.loginType, auth.tenantName, history]);

  useEffect(() => {
    const checkDate = async () => {
      // Can only invalidate the session (logout) when online and authenticated.
      if (!isOnline || !auth.isAuthenticated || auth.id === 0) return;
      let invalidDate: string | null = null;
      try {
        // Get the invalidation date.
        invalidDate = await getInvalidationDate();
      } catch (error) {
        const err = error as Error;
        if (err.name === "ExpiredTokenException") {
          try {
            // AWS credentials expired so refresh and try again.
            await refresh();
            invalidDate = await getInvalidationDate();
          } catch (retryError) {
            const retryErr = retryError as Error;
            // If the refresh tokens themselves were invalid or corrupted, logout.
            if (retryErr.name === "UnrecognizedClientException") {
              console.error("Token became invalid during refresh execution. Logging out.");
              logout();
              return;
            } else {
              console.error("Error getting invalidation date after refresh", retryErr);
            }
          }
        }
        // Handle malformed/rejected client sessions.
        else if (err.name === "UnrecognizedClientException") {
          console.error("AWS credentials rejected as structurally invalid. Forcing logout.");
          logout();
          return;
        }
      }
      if (invalidDate === null || invalidDate === "") return;
      // Log out if the session is invalid.
      if (moment(invalidDate).isBefore(moment())) {
        // Update invalidation date.
        // New invalidation date is set to the next upcoming 1st September.
        const newDate = seasonStartDate;
        await updateInvalidationDate(`${auth.tenantName}-${auth.id}`, newDate);
        logout();
        setValid(false);
      } else {
        setValid(true);
      }
    };
    checkDate();
  }, [
    logout,
    auth.isAuthenticated,
    isOnline,
    auth.tenantName,
    auth.id,
    updateInvalidationDate,
    getInvalidationDate,
    refresh,
    seasonStartDate,
  ]);

  // Loading state.
  if (loading || auth.loading) {
    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div>
          <Spinner />
        </div>
      </div>
    );
  }

  // Valid date.
  if (valid) {
    return <>{children}</>;
  }

  // Invalid date. Logout will redirect to login page.
  return <></>;
};

const mapStateToProps = ({ auth }: { auth: IAuthReducer }) => {
  return { auth };
};
export default compose<React.ComponentType<Partial<Props>>>(
  connect(mapStateToProps, { Logout, Refresh })
)(SessionGuard);
